import axios, { AxiosRequestConfig } from 'axios';
import { camelizeKeys } from 'xcase';

import { updateBucketQuota } from '~shared/stores/aiQuotaStore';
import type { AiQuotaBucket } from '~shared/stores/aiQuotaStore';
import { ErrorHandler } from '~shared/utils/ResponseHandler';

import { baseURL } from './urls';

const axiosConfig: AxiosRequestConfig = {
  baseURL,
  withCredentials: true,
};

const localAxios = axios.create(axiosConfig);

// Always configure camelizeKeys response transformation
localAxios.interceptors.response.use((response) => {
  if (response.data) {
    response.data = camelizeKeys(response.data);
  }
  return response;
});

const VALID_BUCKETS = new Set<AiQuotaBucket>([
  'questions',
  'imports',
  'insights',
  'question_assist',
]);

function parseQuotaHeaders(headers: Record<string, string>): void {
  try {
    const rawBucket = headers['x-ai-quota-bucket'];
    if (!rawBucket || !VALID_BUCKETS.has(rawBucket as AiQuotaBucket)) {
      return;
    }

    const bucket = rawBucket as AiQuotaBucket;
    const weeklyLimit = Number(headers['x-ai-quota-weekly-limit']);
    const weeklyRemaining = Number(headers['x-ai-quota-weekly-remaining']);

    if (!Number.isFinite(weeklyLimit) || !Number.isFinite(weeklyRemaining)) {
      return;
    }

    const weeklyResetAt = headers['x-ai-quota-weekly-reset-at'] ?? '';
    if (!weeklyResetAt || Number.isNaN(new Date(weeklyResetAt).getTime())) {
      return;
    }

    updateBucketQuota({
      bucket,
      weeklyLimit,
      weeklyRemaining,
      weeklyResetAt,
    });
  } catch (error) {
    console.warn('[aiQuota] Failed to parse quota headers', error);
  }
}

localAxios.interceptors.response.use(
  (response) => {
    parseQuotaHeaders(response.headers);
    return response;
  },
  (error) => {
    if (error.response?.headers) {
      parseQuotaHeaders(error.response.headers);
    }
    return Promise.reject(error);
  }
);

/**
 * Socrative has an error handler that redirects on errors.
 */
export function setupSocrativeInterceptors() {
  localAxios.interceptors.response.use(
    (response) => response,
    (error) => {
      ErrorHandler(error);
      return Promise.reject(error);
    }
  );
}

export default localAxios;
